import React, { useState } from 'react';
import { Wand2, X, Info, Music, Zap, Cloud, Flame, Globe, Star, Mic, Disc } from 'lucide-react';
import { TEMPLATES } from '@/state/autoArranger';

const TEMPLATE_ICONS = {
  'classic-boom-bap': Mic,
  'trap-wave': Zap,
  'lofi-chill': Cloud,
  'quick-flip': Star,
  'latin-heat': Globe,
  'pop-structure': Music,
  'drill-pressure': Flame,
  'freestyle-flow': Disc,
};

const TEMPLATE_COLORS = {
  'classic-boom-bap': '#FF2D78',
  'trap-wave': '#FF8A00',
  'lofi-chill': '#B44DFF',
  'quick-flip': '#00D4AA',
  'latin-heat': '#FF8A00',
  'pop-structure': '#4DA6FF',
  'drill-pressure': '#FF2D78',
  'freestyle-flow': '#39FF14',
};

export const AutoArrangerModal = ({ isOpen, onClose, onSelectTemplate }) => {
  const [hoveredId, setHoveredId] = useState(null);

  if (!isOpen) return null;

  return (
    <div data-testid="auto-arranger-modal" className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div
        className="flex flex-col"
        style={{
          width: 580,
          maxHeight: '85vh',
          background: 'var(--bg-card)',
          borderRadius: 16,
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <Wand2 size={18} style={{ color: 'var(--purple)' }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Auto Arranger</span>
          </div>
          <button data-testid="close-auto-arranger" onClick={onClose} className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--bg-elevated)', color: 'var(--text-dim)' }}>
            <X size={14} />
          </button>
        </div>

        {/* Description */}
        <div className="px-5 py-3 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Choose a song structure template. Your loops will be automatically arranged into a full beat.
          </p>
        </div>

        {/* Template Cards */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
          {TEMPLATES.map((tmpl) => {
            const Icon = TEMPLATE_ICONS[tmpl.id] || Music;
            const accent = TEMPLATE_COLORS[tmpl.id] || '#00D4AA';
            const isHovered = hoveredId === tmpl.id;

            return (
              <div key={tmpl.id} className="relative">
                <button
                  data-testid={`template-${tmpl.id}`}
                  onClick={() => { onSelectTemplate?.(tmpl.id); onClose?.(); }}
                  onMouseEnter={() => setHoveredId(tmpl.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className="flex items-start gap-3 px-4 py-3 w-full text-left transition-all"
                  style={{
                    background: 'var(--bg-elevated)',
                    borderRadius: 10,
                    border: `1px solid ${isHovered ? accent : 'var(--border)'}`,
                  }}
                >
                  {/* Icon */}
                  <div
                    className="shrink-0 flex items-center justify-center rounded-lg mt-0.5"
                    style={{ width: 36, height: 36, background: `${accent}15`, color: accent }}
                  >
                    <Icon size={18} />
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-1 min-w-0 gap-1">
                    {/* Title row */}
                    <div className="flex items-center gap-2">
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                        {tmpl.name}
                      </span>
                      <span
                        style={{
                          fontSize: 10, fontWeight: 600, color: accent,
                          background: `${accent}15`, padding: '1px 8px', borderRadius: 22,
                        }}
                      >
                        {tmpl.totalBars} bars
                      </span>
                    </div>

                    {/* Section structure pills */}
                    <div className="flex flex-wrap gap-1">
                      {tmpl.sections.map((sec, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 9, fontWeight: 500,
                            color: 'var(--text-dim)',
                            background: 'var(--bg)',
                            padding: '1px 6px',
                            borderRadius: 4,
                          }}
                        >
                          {sec.name}({sec.bars})
                        </span>
                      ))}
                    </div>

                    {/* Teaching concept */}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>
                      {tmpl.teachingConcept}
                    </span>
                  </div>

                  {/* Info tooltip trigger */}
                  <div className="shrink-0 mt-1 relative group">
                    <Info size={14} style={{ color: 'var(--text-dim)' }} />
                  </div>
                </button>

                {/* Educational Tooltip — Section 8.3 */}
                {isHovered && (
                  <div
                    data-testid={`tooltip-${tmpl.id}`}
                    className="absolute right-0 top-0 z-10"
                    style={{
                      transform: 'translateX(calc(100% + 8px))',
                      width: 220,
                      padding: '10px 14px',
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Info size={12} style={{ color: accent }} />
                      <span style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: 'uppercase' }}>
                        Tip
                      </span>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      {tmpl.tooltip}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AutoArrangerModal;
